import { useState } from "react";
import { toast } from "sonner";
import { ArrowUpRight, Mail, Phone, MapPin } from "lucide-react";
import { Container, CodeTag, SectionHeader } from "@/components/Section";
import { Reveal, LineReveal } from "@/components/motion";
import Seo from "@/components/Seo";
import { submitInquiry } from "@/lib/api";

function Field({ label, testid, value, onChange, type = "text", required }) {
  return (
    <div>
      <label className="label-mono text-neutral-700">
        {label} {required && <span className="text-[#FF4F00]">*</span>}
      </label>
      <input
        data-testid={testid}
        type={type}
        value={value}
        onChange={onChange}
        className="mt-2 w-full px-4 py-3 bg-white brut-border font-sans text-sm focus:outline-none focus:border-[#FF4F00] focus:ring-2 focus:ring-[#FF4F00]"
      />
    </div>
  );
}

export default function Contact() {
  const [form, setForm] = useState({
    full_name: "",
    email: "",
    phone: "",
    company: "",
    project_type: "",
    budget: "",
    message: "",
  });
  const [submitting, setSubmitting] = useState(false);

  const handleChange = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.full_name || !form.email || !form.message) {
      toast.error("Name, email and message are required.");
      return;
    }
    setSubmitting(true);
    try {
      await submitInquiry(form);
      toast.success("Inquiry sent. Our estimating team will respond within two business days.");
      setForm({ full_name: "", email: "", phone: "", company: "", project_type: "", budget: "", message: "" });
    } catch {
      toast.error("Something went wrong. Try again or email estimating@aprasim.ca");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div data-testid="contact-page">
      <Seo
        title="Contact APRASIM | Request a Bid — Civil Contractor in Chilliwack, BC"
        description="Send tender packages, RFPs or inquiries to APRASIM Contracting Corp. in Chilliwack, BC. Our estimating team responds within two business days."
        path="/contact"
      />
      <section className="border-b-2 border-black">
        <Container className="py-16 sm:py-24 grid grid-cols-12 gap-10">
          {/* LEFT */}
          <div className="col-span-12 lg:col-span-5">
            <Reveal y={20}>
              <CodeTag>CONTACT / OPEN FILES</CodeTag>
            </Reveal>
            <LineReveal
              className="mt-6 font-display text-5xl sm:text-7xl uppercase tracking-tighter leading-[0.92]"
              delay={0.15}
              lines={[<>Let&apos;s talk</>, <><span className="text-[#FF4F00]">numbers</span></>]}
            />
            <Reveal delay={0.4}>
              <p className="mt-6 text-lg text-neutral-700 leading-relaxed max-w-md">
                Send tender packages, RFPs or general inquiries. Estimating team responds within 2 business days.
              </p>
            </Reveal>

            <Reveal delay={0.2} className="mt-10 brut-border p-6 bg-[#E5E5DF] space-y-5">
              <div className="flex items-start gap-3">
                <MapPin className="w-5 h-5 text-[#FF4F00] mt-0.5" />
                <div>
                  <div className="label-mono">Office</div>
                  <p className="mt-1 font-mono text-sm">104-8645 Young Rd, Chilliwack, BC</p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <Phone className="w-5 h-5 text-[#FF4F00] mt-0.5" />
                <div>
                  <div className="label-mono">Phone</div>
                  <a href="tel:+17782283264" data-testid="contact-phone-link" className="mt-1 font-mono text-sm link-underline">+1 (778) 228-3264</a>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <Mail className="w-5 h-5 text-[#FF4F00] mt-0.5" />
                <div>
                  <div className="label-mono">Email</div>
                  <div className="mt-1 font-mono text-sm space-y-1">
                    <div><a href="mailto:info@aprasim.ca" data-testid="contact-email-bids" className="link-underline">info@aprasim.ca</a></div>
                    <div><a href="mailto:estimating@aprasim.ca" data-testid="contact-email-careers" className="link-underline">estimating@aprasim.ca</a></div>
                  </div>
                </div>
              </div>
            </Reveal>

            <Reveal delay={0.3} className="mt-6 brut-border p-6 bg-black text-[#F0F0EC]">
              <div className="label-mono text-[#FF4F00]">Office Hours</div>
              <div className="mt-3 grid grid-cols-2 gap-y-2 font-mono text-sm">
                <span>Mon — Fri</span><span className="text-right">07:00 — 17:00</span>
                <span>Saturday</span><span className="text-right">By Appointment</span>
                <span>Sunday</span><span className="text-right">Closed</span>
              </div>
            </Reveal>
          </div>

          {/* RIGHT */}
          <div className="col-span-12 lg:col-span-7">
            <form
              onSubmit={submit}
              data-testid="contact-form"
              className="brut-border bg-[#F0F0EC] p-6 sm:p-10 grid grid-cols-1 sm:grid-cols-2 gap-5"
            >
              <div className="sm:col-span-2 flex items-center justify-between mb-2">
                <div className="label-mono text-[#FF4F00]">Inquiry Form</div>
                <div className="font-mono text-[11px] uppercase tracking-[0.25em] text-neutral-600">
                  /FORM-{new Date().getFullYear()}
                </div>
              </div>
              <Field required label="Full Name" testid="contact-name" value={form.full_name} onChange={handleChange("full_name")} />
              <Field required label="Email" testid="contact-email" type="email" value={form.email} onChange={handleChange("email")} />
              <Field label="Phone" testid="contact-phone" value={form.phone} onChange={handleChange("phone")} />
              <Field label="Company" testid="contact-company" value={form.company} onChange={handleChange("company")} />

              <div>
                <label className="label-mono text-neutral-700">Project Type</label>
                <select
                  data-testid="contact-project-type"
                  value={form.project_type}
                  onChange={handleChange("project_type")}
                  className="mt-2 w-full px-4 py-3 bg-white brut-border font-sans text-sm focus:outline-none focus:border-[#FF4F00] focus:ring-2 focus:ring-[#FF4F00]"
                >
                  <option value="">Select…</option>
                  <option>Civil Infrastructure</option>
                  <option>Site Development</option>
                  <option>Heavy Construction</option>
                  <option>Earthworks</option>
                  <option>Concrete</option>
                  <option>Other</option>
                </select>
              </div>
              <div>
                <label className="label-mono text-neutral-700">Budget Range</label>
                <select
                  data-testid="contact-budget"
                  value={form.budget}
                  onChange={handleChange("budget")}
                  className="mt-2 w-full px-4 py-3 bg-white brut-border font-sans text-sm focus:outline-none focus:border-[#FF4F00] focus:ring-2 focus:ring-[#FF4F00]"
                >
                  <option value="">Select…</option>
                  <option>Under $1M</option>
                  <option>$1M – $5M</option>
                  <option>$5M – $20M</option>
                  <option>$20M – $50M</option>
                  <option>$50M+</option>
                </select>
              </div>

              <div className="sm:col-span-2">
                <label className="label-mono text-neutral-700">
                  Project Details <span className="text-[#FF4F00]">*</span>
                </label>
                <textarea
                  data-testid="contact-message"
                  rows={6}
                  value={form.message}
                  onChange={handleChange("message")}
                  className="mt-2 w-full px-4 py-3 bg-white brut-border font-sans text-sm focus:outline-none focus:border-[#FF4F00] focus:ring-2 focus:ring-[#FF4F00]"
                  placeholder="Scope, location, schedule, attachments to follow by email…"
                />
              </div>
              <div className="sm:col-span-2 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mt-2">
                <p className="font-mono text-[11px] uppercase tracking-[0.25em] text-neutral-600">
                  // Confidential — not used outside estimating team
                </p>
                <button
                  type="submit"
                  disabled={submitting}
                  data-testid="contact-submit-button"
                  className="inline-flex items-center gap-3 bg-black text-white px-7 py-5 font-mono text-xs uppercase tracking-[0.25em] hover:bg-[#FF4F00] hover:text-black transition-colors disabled:opacity-60"
                >
                  {submitting ? "Sending…" : "Send Inquiry"} <ArrowUpRight className="w-4 h-4" />
                </button>
              </div>
            </form>
          </div>
        </Container>
      </section>

      {/* MAP */}
      <section data-testid="contact-map-section" className="border-b-2 border-black">
        <Container className="py-16 sm:py-20">
          <Reveal>
            <SectionHeader
              kicker="Find Us"
              title="The office"
              description="104-8645 Young Rd, Chilliwack, BC — drop by during office hours or book a time."
            />
          </Reveal>
          <Reveal delay={0.15} className="mt-10 brut-border overflow-hidden bg-black">
            <iframe
              title="APRASIM Contracting Corp. — 104-8645 Young Rd, Chilliwack, BC"
              data-testid="contact-map-embed"
              src="https://www.google.com/maps?q=104-8645%20Young%20Rd%2C%20Chilliwack%2C%20BC&output=embed"
              className="w-full h-[420px] sm:h-[480px] block"
              style={{ border: 0, filter: "grayscale(1) contrast(1.05)" }}
              loading="lazy"
              allowFullScreen
              referrerPolicy="no-referrer-when-downgrade"
            />
          </Reveal>
        </Container>
      </section>
    </div>
  );
}
